<template>
  <div class="tw:relative tw:min-w-0 tw:w-full">
    <div
      class="tw:relative tw:min-w-0 tw:w-full tw:rounded-2xl tw:bg-white tw:px-5 tw:py-6 tw:sm:px-7 tw:sm:py-8"
      :class="{ 'tw:cursor-not-allowed': isDisabled }"
      :title="isDisabled ? resolvedDisabledReason : ''"
      @mouseenter="onDisabledBlockHover(true)"
      @mouseleave="onDisabledBlockHover(false)"
    >
      <h3
        v-if="shouldShowTitle"
        class="tw:m-0 tw:text-lg tw:font-bold tw:leading-[150%] tw:text-[#242424]"
      >
        {{ resolvedTitle }}
      </h3>
      <p
        class="tw:mb-5 tw:max-w-full tw:text-base tw:leading-[150%] tw:text-[#48586e] tw:sm:text-lg tw:lg:max-w-[790px]"
        :class="shouldShowTitle ? 'tw:mt-2' : 'tw:mt-0'"
      >
        If you add credentials and one-time codes, the scan can reach authenticated screens and the
        report may include more findings.
      </p>
      <div
        v-for="field in displayedFields"
        :key="field.fieldKey"
        class="tw:mb-3.5 tw:flex tw:min-w-0 tw:w-full tw:flex-col tw:items-start tw:lg:grid tw:lg:grid-cols-[190px_600px_18px] tw:lg:items-center tw:lg:gap-x-4"
      >
        <label
          class="tw:mb-2 tw:m-0 tw:text-sm tw:leading-[150%] tw:text-[#596377] tw:lg:mb-0 tw:lg:w-[190px]"
        >
          {{ field.label }}
        </label>
        <div class="tw:flex tw:w-full tw:items-center tw:gap-4 tw:lg:contents">
          <div
            class="tw:flex tw:min-h-[60px] tw:min-w-0 tw:w-full tw:max-w-full tw:flex-1 tw:items-center tw:rounded-lg tw:bg-[#f2f4f7] tw:px-4 tw:py-3 tw:text-lg tw:text-[#242424] tw:cursor-default tw:lg:w-[600px] tw:lg:max-w-[600px] tw:lg:flex-none"
          >
            {{ getMaskedValue(field) }}
          </div>
          <button
            type="button"
            class="tw:flex tw:h-[18px] tw:w-[18px] tw:shrink-0 tw:items-center tw:justify-center tw:self-center tw:border-0 tw:bg-transparent tw:p-0 tw:text-[#8b97aa] tw:hover:opacity-80 tw:disabled:cursor-default tw:disabled:opacity-50"
            aria-label="remove field"
            :disabled="isDisabled || showSpinner"
            @click="removeField(field.fieldKey)"
          >
            <TrashcanIcon class="tw:block" />
          </button>
        </div>
      </div>
      <div
        class="tw:mt-5 tw:flex tw:w-full tw:max-w-full tw:justify-start tw:gap-2.5 tw:lg:ml-[206px] tw:lg:w-[600px] tw:lg:max-w-[600px] tw:lg:justify-end"
      >
        <button
          v-if="displayedFields.length"
          type="button"
          class="tw:rounded-lg tw:border-0 tw:bg-[#fff0eb] tw:px-5 tw:py-3 tw:text-sm tw:leading-[150%] tw:text-[#fb8d62] tw:transition-opacity tw:hover:opacity-80 tw:disabled:cursor-default tw:disabled:opacity-50"
          :disabled="isDisabled || showSpinner"
          @click="openRemoveAllConfirmModal"
        >
          Remove all
        </button>
        <button
          type="button"
          class="tw:rounded-lg tw:border-0 tw:bg-[#eef3ff] tw:px-5 tw:py-3 tw:text-sm tw:leading-[150%] tw:text-[#517aed] tw:transition-opacity tw:hover:opacity-80 tw:disabled:cursor-default tw:disabled:opacity-50"
          :disabled="isDisabled || showSpinner"
          @click="openAddFieldModal"
        >
          Add field
        </button>
      </div>
      <div
        v-if="isDisabled && isDisabledHintVisible"
        class="tw:pointer-events-none tw:absolute tw:bottom-5 tw:right-5 tw:z-[11] tw:max-w-80 tw:rounded-lg tw:bg-[#222629] tw:px-3 tw:py-2 tw:text-[13px] tw:leading-[18px] tw:text-white"
      >
        {{ resolvedDisabledReason }}
      </div>
    </div>
    <ScannerCredentialFieldsAddFieldModal
      v-if="isAddFieldModalOpen"
      :fields="displayedFields"
      :is-disabled="isDisabled"
      :show-spinner="showSpinner"
      :close-modal="closeAddFieldModal"
      :submit-field="submitField"
    />
    <div
      v-if="isRemoveAllConfirmModalOpen"
      class="tw:fixed tw:inset-0 tw:z-30 tw:flex tw:items-center tw:justify-center tw:bg-[rgba(22,30,45,0.2)] tw:p-5"
      @click.self="closeRemoveAllConfirmModal"
    >
      <div class="tw:w-full tw:max-w-[422px] tw:rounded-lg tw:bg-white tw:p-8">
        <div class="tw:flex tw:w-full tw:flex-col tw:gap-6">
          <div class="tw:flex tw:items-start tw:justify-between tw:gap-3">
            <h4
              class="tw:m-0 tw:text-3xl tw:font-normal tw:leading-8 tw:tracking-[-2px] tw:text-[#222629]"
            >
              Remove all fields
            </h4>
            <button
              type="button"
              class="tw:h-8 tw:w-8 tw:rounded-lg tw:border-0 tw:bg-transparent tw:p-0 tw:text-4xl tw:leading-none tw:text-[#93a1b4]"
              aria-label="close remove all modal"
              :disabled="showSpinner"
              @click="closeRemoveAllConfirmModal"
            >
              ×
            </button>
          </div>
          <p class="tw:m-0 tw:text-sm tw:leading-5 tw:tracking-[-1px] tw:text-[#48586e]">
            This will remove all scanner credential fields.
          </p>
          <div class="tw:flex tw:items-center tw:gap-2">
            <button
              type="button"
              class="tw:h-12 tw:w-full tw:rounded-lg tw:border tw:border-[#e3e8ef] tw:bg-white tw:text-sm tw:leading-6 tw:tracking-[-0.75px] tw:text-[#222629] tw:transition-opacity tw:hover:opacity-90 tw:disabled:cursor-default tw:disabled:opacity-55"
              :disabled="showSpinner"
              @click="closeRemoveAllConfirmModal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="tw:h-12 tw:w-full tw:rounded-lg tw:border-0 tw:bg-[#eb3b2f] tw:text-sm tw:leading-6 tw:tracking-[-0.75px] tw:text-white tw:transition-opacity tw:hover:opacity-90 tw:disabled:cursor-default tw:disabled:opacity-55"
              :disabled="showSpinner"
              @click="removeAllFields"
            >
              Remove all
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="showSpinner"
      class="tw:absolute tw:left-1/2 tw:top-1/2 tw:z-10 tw:flex tw:h-full tw:w-full tw:-translate-x-1/2 tw:-translate-y-1/2 tw:items-center tw:bg-[rgba(255,255,255,0.7)] tw:rounded-2xl"
    >
      <Spinner />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, ref } from 'vue';
import Spinner from '@/components/Spinner.vue';
import TrashcanIcon from '@/assets/images/svg/trashcan.svg?component';
import {
  successNotification,
  failNotification,
} from '@/services/notificationService/notificationService';
import { getMaskedValue } from '@/utils/maskFieldValue';
import { buildScannerCredentialFieldsPayload } from '@/utils/buildScannerCredentialFieldsPayload';
import { useFetchScannerCredentialFields } from '@/_core/entities/scanner-credential-fields/api/useFetchScannerCredentialFields';
import { useUpdateScannerCredentialFields } from '@/_core/entities/scanner-credential-fields/api/useUpdateScannerCredentialFields';
import {
  CUSTOM_FIELD_OPTION,
  DEFAULT_SCANNER_CREDENTIAL_FIELDS_DISABLED_REASON,
  DEFAULT_SCANNER_CREDENTIAL_FIELDS_MODE,
  DEFAULT_SCANNER_CREDENTIAL_FIELDS_TITLE,
} from '@/_core/entities/scanner-credential-fields/models/constants';
import type {
  TNormalizedScannerCredentialField,
  TScannerCredentialFieldsMode,
  TScannerCredentialFieldsQueryPayload,
  TScannerCredentialFieldsUpdatePayload,
} from '@/_core/entities/scanner-credential-fields/models/types';
import ScannerCredentialFieldsAddFieldModal from '@/_core/entities/scanner-credential-fields/ui/ScannerCredentialFieldsAddFieldModal.vue';

type TScannerCredentialFieldsProps = {
  mode?: TScannerCredentialFieldsMode;
  integrationIdProp?: string;
  disabled?: boolean;
  disabledReason?: string;
  title?: string;
  hideTitle?: boolean;
};

const props = defineProps<TScannerCredentialFieldsProps>();

const instance = getCurrentInstance();

const isAddFieldModalOpen = ref(false);
const isRemoveAllConfirmModalOpen = ref(false);
const isDisabledHintVisible = ref(false);

const queryPayload = computed<TScannerCredentialFieldsQueryPayload | null>(() => {
  if ((props.mode ?? DEFAULT_SCANNER_CREDENTIAL_FIELDS_MODE) === 'single-scan') {
    return { mode: 'single-scan' };
  }

  const routeIntegrationId = instance?.proxy?.$route?.params?.id;
  const integrationIdFromRoute = typeof routeIntegrationId === 'string' ? routeIntegrationId : '';
  const integrationId = props.integrationIdProp || integrationIdFromRoute;

  if (!integrationId) {
    return null;
  }

  return {
    mode: 'integration',
    integrationId,
  };
});

const { data: fieldsData, isLoading: isLoadingFields } =
  useFetchScannerCredentialFields(queryPayload);

const { mutateAsync: updateFieldsMutation, isPending: isUpdatingFields } =
  useUpdateScannerCredentialFields();

const fields = computed<TNormalizedScannerCredentialField[]>(() => fieldsData.value ?? []);

const displayedFields = computed<TNormalizedScannerCredentialField[]>(() =>
  fields.value.filter((field: TNormalizedScannerCredentialField) => !!field.value)
);

const showSpinner = computed(() => isLoadingFields.value || isUpdatingFields.value);

const isDisabled = computed(() => props.disabled ?? false);
const shouldShowTitle = computed(() => !props.hideTitle);

const resolvedTitle = computed(() => props.title ?? DEFAULT_SCANNER_CREDENTIAL_FIELDS_TITLE);

const resolvedDisabledReason = computed(() => {
  if (props.disabledReason) {
    return props.disabledReason;
  }

  return DEFAULT_SCANNER_CREDENTIAL_FIELDS_DISABLED_REASON;
});

function openAddFieldModal(): void {
  if (isDisabled.value) {
    return;
  }

  isAddFieldModalOpen.value = true;
}

function onDisabledBlockHover(isHovered: boolean): void {
  if (!isDisabled.value) {
    isDisabledHintVisible.value = false;
    return;
  }

  isDisabledHintVisible.value = isHovered;
}

function closeAddFieldModal(): void {
  isAddFieldModalOpen.value = false;
}

function openRemoveAllConfirmModal(): void {
  if (!displayedFields.value.length) {
    return;
  }

  isRemoveAllConfirmModalOpen.value = true;
}

function closeRemoveAllConfirmModal(): void {
  isRemoveAllConfirmModalOpen.value = false;
}

async function updateFields(fields: TNormalizedScannerCredentialField[]): Promise<boolean> {
  if (isDisabled.value) {
    return false;
  }

  if (showSpinner.value) {
    return false;
  }

  const payloadBase = queryPayload.value;

  if (!payloadBase) {
    return false;
  }

  const payload: TScannerCredentialFieldsUpdatePayload = {
    ...payloadBase,
    scannerCredentialFields: buildScannerCredentialFieldsPayload(
      fields,
      CUSTOM_FIELD_OPTION.fieldKey
    ),
  };

  try {
    await updateFieldsMutation(payload);
    successNotification.call(instance?.proxy, 'settingsUpdated');
    return true;
  } catch {
    failNotification.call(instance?.proxy);
    return false;
  }
}

async function submitField(field: TNormalizedScannerCredentialField): Promise<boolean> {
  const nextFields = [...displayedFields.value, field];

  return updateFields(nextFields);
}

async function removeField(fieldKey: string): Promise<void> {
  const nextFields = displayedFields.value.filter(
    (field: TNormalizedScannerCredentialField) => field.fieldKey !== fieldKey
  );

  if (nextFields.length === displayedFields.value.length) {
    return;
  }

  await updateFields(nextFields);
}

async function removeAllFields(): Promise<void> {
  if (!displayedFields.value.length) {
    return;
  }

  const isUpdated = await updateFields([]);

  if (!isUpdated) {
    return;
  }

  closeRemoveAllConfirmModal();
}
</script>
